import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DashboardLayout } from "@/pages/Dashboard";
import { getErrToast } from "@/utils/helpers";
import {
  authenticateAnthroAi,
  getChatHistoryBySession,
  getChatSessions,
  postChatMessage,
  type ChatPostResponse,
} from "../api";
import { ANTHRO_AI_DEFAULT_ALLOWED_ORG_IDS } from "../config";
import { AIChatLayout } from "../components/AIChatLayout";
import { AI_CHAT_MAX_ATTEMPTS, AI_CHAT_ROUTE } from "../constants";
import type { ChatMessage, ChatSendError, ChatThread } from "../types";
import {
  createEmptyThread,
  formatChatTime,
  iconForTitle,
  isPendingSessionId,
  mapSessionListToThreads,
  mergeSessionMessages,
  parseChatContent,
  titleFromPrompt,
} from "../utils";

function assertChatResult(result: ChatPostResponse) {
  const sessionId = String(result.session_id || "").trim();
  const replyText = String(result.reply ?? "").trim();
  if (!sessionId) throw new Error("No session id returned from chat.");
  if (!replyText) throw new Error("No reply received.");
  return { sessionId, replyText };
}

export default function AIChatPage() {
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [historyQuery, setHistoryQuery] = useState("");
  const [draft, setDraft] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [sendError, setSendError] = useState<ChatSendError | null>(null);

  const chatAttemptCountRef = useRef(0);

  const activeThread = useMemo(
    () => threads.find((t) => t.id === activeThreadId) ?? null,
    [threads, activeThreadId],
  );

  const activeSendError = useMemo(() => {
    if (!sendError || !activeThreadId) return null;
    return sendError.threadId === activeThreadId ? sendError : null;
  }, [sendError, activeThreadId]);

  const loadSessions = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const sessions = await getChatSessions();
      setThreads(mapSessionListToThreads(sessions));
    } catch (err) {
      getErrToast(err);
      setThreads([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadSessionMessages = useCallback(async (sessionId: string) => {
    if (isPendingSessionId(sessionId)) return;

    setSessionLoading(true);
    try {
      const data = await getChatHistoryBySession(sessionId);
      setThreads((prev) =>
        prev.map((thread) =>
          thread.id === sessionId
            ? mergeSessionMessages(thread, data.messages)
            : thread,
        ),
      );
    } catch (err) {
      getErrToast(err);
    } finally {
      setSessionLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function initChat() {
      setHistoryLoading(true);
      try {
        await authenticateAnthroAi();
        if (cancelled) return;
        setAuthReady(true);
        await loadSessions();
      } catch (err) {
        if (!cancelled) {
          getErrToast(err);
          setThreads([]);
          setHistoryLoading(false);
        }
      }
    }

    void initChat();
    return () => {
      cancelled = true;
    };
  }, [loadSessions]);

  const applyAssistantReply = useCallback(
    (
      localThreadId: string,
      sessionId: string,
      nextTitle: string,
      replyText: string,
      createdAt?: string,
    ) => {
      const parsed = parseChatContent(replyText, "assistant");
      const assistantMessage: ChatMessage = {
        id: `${sessionId}-assistant-${Date.now()}`,
        role: "assistant",
        type: parsed.type,
        text: parsed.text,
        time: formatChatTime(createdAt || new Date()),
        intro: parsed.intro,
        attachment: parsed.attachment,
        table: parsed.table,
      };

      setThreads((prev) =>
        prev.map((item) =>
          item.id === localThreadId
            ? {
                ...item,
                id: sessionId,
                title: nextTitle,
                icon: iconForTitle(nextTitle),
                group: "today",
                messages: [...item.messages, assistantMessage],
              }
            : item,
        ),
      );
      setActiveThreadId(sessionId);
      setSendError(null);
      chatAttemptCountRef.current = 0;
    },
    [],
  );

  const executeChatRequest = useCallback(
    async (params: {
      message: string;
      localThreadId: string;
      apiSessionId: string;
      nextTitle: string;
      resetAttemptCount?: boolean;
    }) => {
      const { message, localThreadId, apiSessionId, nextTitle, resetAttemptCount } =
        params;

      if (resetAttemptCount) {
        chatAttemptCountRef.current = 0;
      }

      setSendError(null);
      setIsGenerating(true);

      while (chatAttemptCountRef.current < AI_CHAT_MAX_ATTEMPTS) {
        chatAttemptCountRef.current += 1;
        try {
          const result = await postChatMessage({
            message,
            session_id: apiSessionId || undefined,
            allowed_org_ids: ANTHRO_AI_DEFAULT_ALLOWED_ORG_IDS,
          });

          const { sessionId, replyText } = assertChatResult(result);
          applyAssistantReply(
            localThreadId,
            sessionId,
            nextTitle,
            replyText,
            result.created_at,
          );
          setIsGenerating(false);
          return;
        } catch {
          // Auto-retry until max attempts; no user-facing error yet.
        }
      }

      setSendError({
        threadId: localThreadId,
        messageText: message,
        apiSessionId,
        nextTitle,
      });
      setIsGenerating(false);
    },
    [applyAssistantReply],
  );

  const handleNewChat = () => {
    setActiveThreadId(null);
    setDraft("");
    setIsGenerating(false);
    setSendError(null);
    chatAttemptCountRef.current = 0;
  };

  const handleSelectThread = (id: string) => {
    setIsGenerating(false);
    setActiveThreadId(id);
    const thread = threads.find((t) => t.id === id);
    if (thread && thread.messages.length === 0) {
      void loadSessionMessages(id);
    }
  };

  const handleSend = async () => {
    const trimmed = draft.trim();
    if (!trimmed || isGenerating || !authReady) return;

    const pending = activeThread ?? createEmptyThread();
    const localId = pending.id;
    const apiSessionId = isPendingSessionId(localId) ? "" : localId;

    const userParsed = parseChatContent(trimmed, "user");
    const userMessage: ChatMessage = {
      id: `${localId}-user-${Date.now()}`,
      role: "user",
      type: userParsed.type,
      text: userParsed.text,
      time: formatChatTime(),
    };

    const nextTitle =
      pending.title === "New Chat" ? titleFromPrompt(trimmed) : pending.title;

    setThreads((prev) => {
      const has = prev.some((t) => t.id === localId);
      const base = has ? prev : [pending, ...prev];
      return base.map((item) =>
        item.id === localId
          ? {
              ...item,
              title: nextTitle,
              icon: iconForTitle(nextTitle),
              group: "today",
              messages: [...item.messages, userMessage],
            }
          : item,
      );
    });
    setActiveThreadId(localId);
    setDraft("");

    await executeChatRequest({
      message: trimmed,
      localThreadId: localId,
      apiSessionId,
      nextTitle,
      resetAttemptCount: true,
    });
  };

  const handleRetrySend = useCallback(() => {
    if (!activeSendError || isGenerating || !authReady) return;

    void executeChatRequest({
      message: activeSendError.messageText,
      localThreadId: activeSendError.threadId,
      apiSessionId: activeSendError.apiSessionId,
      nextTitle: activeSendError.nextTitle,
      resetAttemptCount: true,
    });
  }, [activeSendError, authReady, executeChatRequest, isGenerating]);

  return (
    <DashboardLayout
      activePage={AI_CHAT_ROUTE}
      className="p-0 overflow-hidden flex flex-col"
    >
      <div className="min-h-0 flex-1 h-full">
        <AIChatLayout
          threads={threads}
          activeThreadId={activeThreadId}
          historyQuery={historyQuery}
          draft={draft}
          isGenerating={isGenerating}
          isHistoryLoading={historyLoading}
          isSessionLoading={sessionLoading}
          sendError={activeSendError}
          onRetrySend={handleRetrySend}
          onHistoryQueryChange={setHistoryQuery}
          onSelectThread={handleSelectThread}
          onNewChat={handleNewChat}
          onDraftChange={setDraft}
          onSend={() => void handleSend()}
        />
      </div>
    </DashboardLayout>
  );
}
